<template>
  <div>
    <div
      class="upload-dropzone p-4 mb-3 text-center border rounded"
      :class="{ 'border-primary bg-light': dragActive, 'border-secondary': !dragActive, 'has-file': !!file, 'is-dragging': dragActive }"
      :role="file ? null : 'button'"
      :tabindex="file ? -1 : 0"
      :aria-label="file ? null : t('features.zaku_product_request_upload.dropzone.aria_label')"
      @dragenter.prevent="dragActive = true"
      @dragover.prevent="dragActive = true"
      @dragleave.prevent="dragActive = false"
      @drop.prevent="handleDrop"
      @click="openFilePicker"
      @keydown.enter.prevent="activateDropzone"
      @keydown.space.prevent="activateDropzone"
    >
      <input
        ref="fileInput"
        type="file"
        accept=".csv,text/csv"
        aria-hidden="true"
        tabindex="-1"
        class="d-none"
        @change="handleFileChange"
      >
      <div v-if="!file">
        <i class="fas fa-cloud-upload-alt fa-2x text-muted mb-2" aria-hidden="true"></i>
        <p class="mb-1" v-html="t('features.zaku_product_request_upload.dropzone.prompt_html')"></p>
        <p class="mb-0">
          <button
            type="button"
            class="btn btn-link btn-sm p-0"
            :aria-description="t('features.zaku_product_request_upload.dropzone.required_columns', { columns: requiredColumns.join(', ') })"
            @click.stop="showAllColumns = true"
          >
            {{ t('features.zaku_product_request_upload.dropzone.help_cta') }}
          </button>
        </p>
      </div>
      <div v-else @click.stop>
        <p class="mb-1">
          <i class="fas fa-file-csv text-primary" aria-hidden="true"></i>
          <strong>{{ file.name }}</strong>
          <span class="text-muted small">({{ formatBytes(file.size) }})</span>
        </p>
        <p class="mb-0">
          <button type="button" class="btn btn-sm btn-outline-secondary mr-1" @click="openFilePicker">
            <i class="fas fa-exchange-alt" aria-hidden="true"></i> {{ t('features.zaku_product_request_upload.dropzone.replace') }}
          </button>
          <button type="button" class="btn btn-sm btn-outline-danger" @click="clearFile">
            <i class="fas fa-times" aria-hidden="true"></i> {{ t('features.zaku_product_request_upload.dropzone.clear') }}
          </button>
        </p>
      </div>
    </div>

    <div class="mb-3">
      <a :href="csvTemplateUrl" class="btn btn-outline-secondary btn-sm">
        <i class="fas fa-download" aria-hidden="true"></i> {{ t('features.zaku_product_request_upload.controls.download_template') }}
      </a>
      <button
        type="button"
        class="btn btn-outline-secondary btn-sm ml-2"
        @click="showAllColumns = !showAllColumns"
      >
        {{ showAllColumns ? t('features.zaku_product_request_upload.controls.hide_columns') : t('features.zaku_product_request_upload.controls.show_columns') }}
      </button>
    </div>

    <div v-if="showAllColumns" class="card card-body mb-3 p-2 columns-panel">
      <div v-if="formatGuideUrl" class="mb-2">
        <a :href="formatGuideUrl" target="_blank" rel="noopener" class="small">
          {{ t('features.zaku_product_request_upload.columns_panel.full_guide') }}
          <i class="fas fa-external-link-alt ml-1" aria-hidden="true"></i>
        </a>
      </div>
      <div class="table-responsive">
        <table class="table table-sm mb-0">
          <thead>
            <tr>
              <th>{{ t('features.zaku_product_request_upload.columns_panel.column') }}</th>
              <th style="width: 110px">{{ t('features.zaku_product_request_upload.columns_panel.required') }}</th>
              <th style="width: 200px">{{ t('features.zaku_product_request_upload.columns_panel.example') }}</th>
              <th style="width: 220px">{{ t('features.zaku_product_request_upload.columns_panel.format_allowed') }}</th>
              <th>{{ t('features.zaku_product_request_upload.columns_panel.notes') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="col in schema" :key="col.name">
              <td><code translate="no">{{ col.name }}</code></td>
              <td>
                <span v-if="col.required" class="badge badge-primary">{{ t('features.zaku_product_request_upload.badges.required') }}</span>
                <span v-else class="badge badge-secondary">{{ t('features.zaku_product_request_upload.badges.optional') }}</span>
              </td>
              <td class="example">
                <code v-if="col.example" class="small" translate="no">{{ col.example }}</code>
                <span v-else class="text-muted small">—</span>
              </td>
              <td class="format small">
                <div v-if="col.format" translate="no">{{ col.format }}</div>
                <div v-if="col.allowed && col.allowed.length" translate="no">
                  <code v-for="value in col.allowed" :key="value" class="mr-1">{{ value }}</code>
                </div>
                <div v-if="col.range" translate="no">{{ col.range }}</div>
                <span v-if="!col.format && !(col.allowed && col.allowed.length) && !col.range" class="text-muted">—</span>
              </td>
              <td class="small">{{ col.description }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-if="loading" class="alert alert-info" role="status" aria-live="polite">
      <i class="fas fa-spinner fa-spin" aria-hidden="true"></i> {{ t('features.zaku_product_request_upload.status.validating') }}
    </div>

    <div v-if="serverError" class="alert alert-danger" role="alert">
      {{ t('features.zaku_product_request_upload.status.server_error', { message: serverError }) }}
    </div>

    <div v-if="result && !loading">
      <div v-if="result.header_errors.length" class="alert alert-danger" role="alert">
        <strong>{{ t('features.zaku_product_request_upload.status.header_problems') }}</strong>
        <ul class="mb-0">
          <li v-for="(error, i) in result.header_errors" :key="i">{{ error }}</li>
        </ul>
      </div>

      <div v-else-if="result.row_errors.length" class="alert alert-danger" role="alert" aria-live="polite">
        <div class="d-flex align-items-center mb-2">
          <strong>{{ t('features.zaku_product_request_upload.status.row_errors', { count: result.row_errors.length }) }}</strong>
          <span class="ml-2 small">{{ t('features.zaku_product_request_upload.status.row_errors_hint') }}</span>
          <button
            type="button"
            class="btn btn-sm ml-auto"
            :class="pendingEditCount > 0 ? 'btn-primary' : 'btn-outline-secondary'"
            :disabled="loading"
            @click="validate"
          >
            <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading }" aria-hidden="true"></i>
            {{ t('features.zaku_product_request_upload.revalidate') }}
            <span v-if="pendingEditCount > 0" class="badge badge-light ml-1">{{ pendingEditCount }}</span>
          </button>
        </div>
        <div class="table-responsive">
          <table class="table table-sm table-bordered mb-0">
            <thead>
              <tr>
                <th style="width: 60px">{{ t('features.zaku_product_request_upload.errors_table.row') }}</th>
                <th style="width: 140px">{{ t('features.zaku_product_request_upload.errors_table.sku') }}</th>
                <th style="width: 200px">{{ t('features.zaku_product_request_upload.errors_table.column') }}</th>
                <th style="width: 220px">{{ t('features.zaku_product_request_upload.errors_table.fix') }}</th>
                <th>{{ t('features.zaku_product_request_upload.errors_table.message') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(err, i) in result.row_errors" :key="i">
                <td>{{ err.row_number }}</td>
                <td>
                  <code v-if="err.sku" translate="no">{{ err.sku }}</code>
                  <span v-else class="text-muted">—</span>
                </td>
                <td>
                  <code v-if="err.column" translate="no">{{ err.column }}</code>
                  <span v-else class="text-muted">—</span>
                </td>
                <td>
                  <template v-if="err.column && schemaHas(err.column)">
                    <select
                      v-if="allowedValuesFor(err.column).length"
                      class="form-control form-control-sm"
                      :class="{ 'border-warning': isEditPending(err.row_number, err.column) }"
                      :aria-label="t('features.zaku_product_request_upload.errors_table.cell_aria_label', { row: err.row_number, column: err.column })"
                      :value="valueFor(err.row_number, err.column)"
                      @change="onCellEdit(err.row_number, err.column, $event.target.value)"
                    >
                      <option value="">—</option>
                      <option v-for="value in allowedValuesFor(err.column)" :key="value" :value="value">{{ value }}</option>
                    </select>
                    <input
                      v-else
                      type="text"
                      autocomplete="off"
                      spellcheck="false"
                      class="form-control form-control-sm"
                      :class="{ 'border-warning': isEditPending(err.row_number, err.column) }"
                      :inputmode="inputHintFor(err.column).inputmode"
                      :pattern="inputHintFor(err.column).pattern"
                      :maxlength="inputHintFor(err.column).maxlength"
                      :aria-label="t('features.zaku_product_request_upload.errors_table.cell_aria_label', { row: err.row_number, column: err.column })"
                      :value="valueFor(err.row_number, err.column)"
                      @input="onCellEdit(err.row_number, err.column, $event.target.value)"
                    >
                  </template>
                  <span v-else class="text-muted small">{{ t('features.zaku_product_request_upload.errors_table.no_specific_column') }}</span>
                </td>
                <td>{{ err.message }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div v-else class="alert alert-success" role="status" aria-live="polite">
        <i class="fas fa-check-circle" aria-hidden="true"></i>
        {{ t('features.zaku_product_request_upload.status.validation_passed') }}
        {{ t('features.zaku_product_request_upload.status.ready_to_import', { count: validRowCount }) }}
      </div>

      <div v-if="result.rows.length" class="card mb-3">
        <div class="card-header py-2 d-flex flex-wrap align-items-center">
          <strong class="mr-3">{{ t('features.zaku_product_request_upload.preview.title') }}</strong>
          <div class="btn-group btn-group-sm mr-3" role="group">
            <button
              v-for="chip in filterChips"
              :key="chip.value"
              type="button"
              class="btn"
              :class="filter === chip.value ? 'btn-primary' : 'btn-outline-secondary'"
              @click="filter = chip.value"
            >
              {{ chip.label }} ({{ chip.count }})
            </button>
          </div>
          <input
            v-model="search"
            type="search"
            name="zaku_product_request_preview_search"
            autocomplete="off"
            spellcheck="false"
            class="form-control form-control-sm ml-auto"
            :aria-label="t('features.zaku_product_request_upload.preview.search_placeholder')"
            :placeholder="t('features.zaku_product_request_upload.preview.search_placeholder')"
            style="max-width: 240px"
          >
        </div>
        <div class="table-responsive">
          <table class="table table-sm table-bordered table-striped mb-0">
            <thead>
              <tr>
                <th>{{ t('features.zaku_product_request_upload.preview.headers.row') }}</th>
                <th>{{ t('features.zaku_product_request_upload.preview.headers.company') }}</th>
                <th>{{ t('features.zaku_product_request_upload.preview.headers.sku') }}</th>
                <th>{{ t('features.zaku_product_request_upload.preview.headers.sku_suffix') }}</th>
                <th>{{ t('features.zaku_product_request_upload.preview.headers.quantity') }}</th>
                <th>{{ t('features.zaku_product_request_upload.preview.headers.price') }}</th>
                <th>{{ t('features.zaku_product_request_upload.preview.headers.delivery') }}</th>
                <th>{{ t('features.zaku_product_request_upload.preview.headers.comment') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in pagedRows" :key="row.row_number" :class="{ 'table-warning': !row.valid }">
                <td>{{ row.row_number }}</td>
                <td><code translate="no">{{ row.company_code }}</code></td>
                <td><code translate="no">{{ row.sku }}</code></td>
                <td>
                  <code v-if="row.sku_suffix" translate="no">{{ row.sku_suffix }}</code>
                  <span v-else class="text-muted">—</span>
                </td>
                <td>{{ row.quantity }}</td>
                <td>
                  <span v-if="row.price_per_item">¥{{ row.price_per_item }}</span>
                  <span v-else class="text-muted">—</span>
                </td>
                <td>{{ formatDelivery(row) }}</td>
                <td class="small text-muted">{{ truncate(row.product_request_item_comment) }}</td>
              </tr>
              <tr v-if="filteredRows.length === 0">
                <td colspan="8" class="text-center text-muted">{{ t('features.zaku_product_request_upload.preview.no_match') }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-if="totalPages > 1" class="card-footer py-1 d-flex justify-content-between align-items-center">
          <span class="small text-muted">
            {{ t('features.zaku_product_request_upload.preview.page_info', { page: page, total: totalPages, count: filteredRows.length }) }}
          </span>
          <div class="btn-group btn-group-sm">
            <button type="button" class="btn btn-outline-secondary" :disabled="page === 1" @click="page--">{{ t('features.zaku_product_request_upload.preview.previous') }}</button>
            <button type="button" class="btn btn-outline-secondary" :disabled="page === totalPages" @click="page++">{{ t('features.zaku_product_request_upload.preview.next') }}</button>
          </div>
        </div>
      </div>
    </div>

    <form ref="submitForm" :action="createUrl" method="post" enctype="multipart/form-data" class="d-none">
      <input type="hidden" name="authenticity_token" :value="csrfToken">
      <input ref="hiddenFileInput" type="file" name="csv_file">
    </form>

    <div class="d-flex justify-content-end">
      <button
        type="button"
        class="btn btn-primary"
        :disabled="!canSubmit"
        @click="submit"
      >
        <span v-if="submitting"><i class="fas fa-spinner fa-spin" aria-hidden="true"></i> {{ t('features.zaku_product_request_upload.submit.uploading') }}</span>
        <span v-else-if="result && !result.valid">{{ t('features.zaku_product_request_upload.submit.cannot_import') }}</span>
        <span v-else-if="result && result.valid">{{ t('features.zaku_product_request_upload.submit.import_count', { count: validRowCount }) }}</span>
        <span v-else>{{ t('features.zaku_product_request_upload.submit.pick_csv_first') }}</span>
      </button>
    </div>
  </div>
</template>

<script>
const PAGE_SIZE = 25

export default {
  name: 'ZakuProductRequestsUploader',
  inject: ['i18n'],
  props: {
    schema: { type: Array, required: true },
    validateUrl: { type: String, required: true },
    createUrl: { type: String, required: true },
    csvTemplateUrl: { type: String, required: true },
    formatGuideUrl: { type: String, default: null },
    csrfToken: { type: String, required: true }
  },
  data() {
    return {
      t: (key, options) => this.i18n.t(key, options),
      file: null,
      result: null,
      loading: false,
      submitting: false,
      serverError: null,
      dragActive: false,
      showAllColumns: false,
      filter: 'all',
      search: '',
      page: 1,
      editedRows: {},
      pendingEdits: {}
    }
  },
  mounted() {
    if (typeof localStorage !== 'undefined' && !localStorage.getItem('zaku-product-requests-cols-seen')) {
      this.showAllColumns = true
      try { localStorage.setItem('zaku-product-requests-cols-seen', '1') } catch (_) {}
    }
  },
  computed: {
    requiredColumns() {
      return this.schema.filter(c => c.required).map(c => c.name)
    },
    schemaNames() {
      return new Set(this.schema.map(c => c.name))
    },
    schemaByName() {
      const map = {}
      for (const col of this.schema) map[col.name] = col
      return map
    },
    validRowCount() {
      return (this.result?.rows || []).filter(r => r.valid).length
    },
    filterChips() {
      const rows = this.result?.rows || []
      return [
        { value: 'all', label: this.t('features.zaku_product_request_upload.preview.chips.all'), count: rows.length },
        { value: 'errors', label: this.t('features.zaku_product_request_upload.preview.chips.errors'), count: rows.filter(r => !r.valid).length }
      ]
    },
    filteredRows() {
      const rows = this.result?.rows || []
      const term = this.search.trim().toLowerCase()
      return rows.filter(row => {
        if (this.filter === 'errors' && row.valid) return false
        if (term) {
          const haystack = [row.sku, row.sku_suffix, row.company_code].filter(Boolean).join(' ').toLowerCase()
          if (!haystack.includes(term)) return false
        }
        return true
      })
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.filteredRows.length / PAGE_SIZE))
    },
    pagedRows() {
      const start = (this.page - 1) * PAGE_SIZE
      return this.filteredRows.slice(start, start + PAGE_SIZE)
    },
    canSubmit() {
      return this.file && this.result && this.result.valid && !this.submitting && !this.loading
    },
    hasEdits() {
      return Object.keys(this.editedRows).length > 0
    },
    pendingEditCount() {
      return Object.values(this.pendingEdits).reduce((sum, byCol) => sum + Object.keys(byCol).length, 0)
    }
  },
  watch: {
    filter() { this.page = 1 },
    search() { this.page = 1 }
  },
  methods: {
    schemaHas(columnName) {
      return this.schemaNames.has(columnName)
    },
    allowedValuesFor(columnName) {
      const col = this.schemaByName[columnName]
      return (col && col.allowed) || []
    },
    inputHintFor(columnName) {
      const col = this.schemaByName[columnName]
      if (!col || !col.format) return {}
      if (col.format === 'MMDD, future weekday') return { inputmode: 'numeric', pattern: '\\d{4}', maxlength: 4 }
      if (col.format === 'YYYY, current year or future') return { inputmode: 'numeric', pattern: '\\d{4}', maxlength: 4 }
      return {}
    },
    openFilePicker() {
      this.$refs.fileInput.click()
    },
    activateDropzone() {
      if (!this.file) this.openFilePicker()
    },
    handleFileChange(event) {
      const file = event.target.files[0]
      if (file) this.setFile(file)
    },
    handleDrop(event) {
      this.dragActive = false
      const file = event.dataTransfer.files[0]
      if (file) this.setFile(file)
    },
    setFile(file) {
      this.file = file
      this.result = null
      this.serverError = null
      this.page = 1
      this.resetEdits()
      this.validate()
    },
    clearFile() {
      if (this.hasEdits && !window.confirm(this.t('features.zaku_product_request_upload.dropzone.clear_confirm'))) return
      this.file = null
      this.result = null
      this.serverError = null
      this.resetEdits()
      this.$refs.fileInput.value = ''
    },
    resetEdits() {
      this.editedRows = {}
      this.pendingEdits = {}
    },
    formatBytes(bytes) {
      if (bytes < 1024) return `${bytes} B`
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
      return `${(bytes / 1024 / 1024).toFixed(1)} MB`
    },
    formatDelivery(row) {
      const year = row.planned_delivery_date_year
      const md = row.planned_delivery_date_month_and_day
      if (!year || !md || md.length < 4) return '—'
      return `${year}-${md.slice(0, 2)}-${md.slice(2, 4)}`
    },
    truncate(text, max = 40) {
      if (!text) return ''
      return text.length > max ? `${text.slice(0, max)}…` : text
    },
    valueFor(rowNumber, columnName) {
      const edits = this.editedRows[rowNumber]
      if (edits && Object.prototype.hasOwnProperty.call(edits, columnName)) {
        return edits[columnName]
      }
      const row = (this.result?.rows || []).find(r => r.row_number === rowNumber)
      const original = row?.cells?.[columnName]
      return original == null ? '' : String(original)
    },
    isEditPending(rowNumber, columnName) {
      return this.pendingEdits[rowNumber]?.[columnName] === true
    },
    onCellEdit(rowNumber, columnName, value) {
      if (!this.editedRows[rowNumber]) this.editedRows[rowNumber] = {}
      this.editedRows[rowNumber][columnName] = value

      if (!this.pendingEdits[rowNumber]) this.pendingEdits[rowNumber] = {}
      this.pendingEdits[rowNumber][columnName] = true
    },
    csvEscape(value) {
      if (value == null) return ''
      const s = String(value)
      if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`
      return s
    },
    buildEditedCsv() {
      const headers = this.schema.map(c => c.name)
      const lines = [headers.map(h => this.csvEscape(h)).join(',')]
      for (const row of this.result?.rows || []) {
        const values = headers.map(name => this.csvEscape(this.valueFor(row.row_number, name)))
        lines.push(values.join(','))
      }
      return lines.join('\n')
    },
    fileToSubmit() {
      if (!this.hasEdits || !this.result) return this.file
      const blob = new Blob([this.buildEditedCsv()], { type: 'text/csv' })
      const filename = this.file?.name || 'edited.csv'
      return new File([blob], filename, { type: 'text/csv' })
    },
    async validate() {
      if (!this.file) return
      this.loading = true
      this.serverError = null
      try {
        const formData = new FormData()
        formData.append('csv_file', this.fileToSubmit())
        const response = await fetch(this.validateUrl, {
          method: 'POST',
          headers: { 'X-CSRF-Token': this.csrfToken, 'Accept': 'application/json' },
          body: formData
        })
        if (!response.ok) {
          this.serverError = `Server returned ${response.status}`
        } else {
          this.result = await response.json()
          this.pendingEdits = {}
        }
      } catch (err) {
        this.serverError = err.message || String(err)
      } finally {
        this.loading = false
      }
    },
    submit() {
      if (!this.canSubmit) return
      this.submitting = true
      const dataTransfer = new DataTransfer()
      dataTransfer.items.add(this.fileToSubmit())
      this.$refs.hiddenFileInput.files = dataTransfer.files
      this.$refs.submitForm.submit()
    }
  }
}
</script>

<style scoped>
.columns-panel .example,
.columns-panel .format {
  font-variant-numeric: tabular-nums;
}
.upload-dropzone {
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;
}
.upload-dropzone.has-file {
  cursor: default;
}
.upload-dropzone.is-dragging {
  user-select: none;
}
.upload-dropzone:focus-visible {
  outline: 2px solid #007bff;
  outline-offset: 2px;
}
@media (prefers-reduced-motion: reduce) {
  .upload-dropzone {
    transition: none;
  }
}
</style>
